import { useNavigate } from "react-router-dom";
import { useRef, useState } from "react";
import {
  endAlignment,
  ErrorMessage,
  inputContainerStyle,
  InputStyled,
} from "./contactForm";
import { Squarebtn } from "../../common/squareBtnProps";
import PasswordInput from "../../../utils/CustomizedLibraries/customPasswordInput";
import { CenterForm } from "../../common/FormLayouts";
import styled from "styled-components";
import {
  ResetFormData,
  ResetFormErrors,
  ResetFormTouched,
} from "../../common/types";
import {
  handleResetBlur,
  resetValidate,
  resetValidateField,
} from "../../../utils/Validations/resetPasswordValidation";
import { useTheme as styledTheme } from "styled-components";
import { useRippleEffect } from "../../../hooks/useRippleEffect";
import handleForgotAndResetSubmit from "../../../utils/FormSubmit/resetPasswordSubmit";
import RequestOTPButton from "../../../utils/FormSubmit/requestOTP";
import { useAuth } from "../../../context/AuthContext";
import { useLoader } from "../../../hooks/useLoader";

const CenterNavTxt = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  p {
    cursor: pointer;
  }
  span {
    font-weight: bold;
  }
`;

export const OTPAndBtn = styled.div`
  display: flex;
  justify-content: space-between;
`;

//1. Forgot password form
export const ForgotPassword: React.FC<{ isResetForm: boolean }> = ({
  isResetForm,
}) => {
  const navigate = useNavigate();
   const { showAlert, closeAlert } = useAuth();//global loader
   const { setShowLoader, LoaderComponent } = useLoader();
  const styledComponentTheme = styledTheme();
  const forgotPasswordRefBtn = useRef<HTMLButtonElement>(null);
  //ripple effect
  useRippleEffect(forgotPasswordRefBtn);

  //initial forgot form data
  const [forgotFormData, setForgotFormData] = useState<Partial<ResetFormData>>({
    email: "",
    otp: "",
  });

  //initial forgot errors
  const [errors, setErrors] = useState<Partial<ResetFormErrors>>({
    email: "",
    otp: "",
  });

  //initial forgot touched state
  const [touched, setTouched] = useState<Partial<ResetFormTouched>>({
    email: false,
    otp: false,
  });

  const handleLoginNav = () => {
    navigate("/dashboard/login");
  };

  // Handle input changes
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;

    if (name === "otp") {
      // Restrict to 6 digits
      const sanitizedValue = value.replace(/[^0-9]/g, "");
      if (sanitizedValue.length > 6) return; // Restrict to 6 digits

      setForgotFormData((prev) => ({
        ...prev,
        otp: value, // Ensure 'otp code' is always a string of digits
      }));
    }

    setForgotFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // console.log("Input Value", value);
    validateFieldWrapper(name as keyof ResetFormData, value);
  };

  const handleBlurWrapper = (
    name: keyof ResetFormData,
    value: string | number
  ) => handleResetBlur(name, value, touched, setTouched, validateFieldWrapper);

  const validateFieldWrapper = (
    name: keyof ResetFormData,
    value: string | number
  ) => {
    resetValidateField(name, value, errors, setErrors, forgotFormData);
  };

  const validateWrapper = () =>
    resetValidate(forgotFormData, errors, setErrors, setTouched);

  const handleEmailSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    handleForgotAndResetSubmit(
      forgotFormData,
      setForgotFormData,
      {
        setTouched,
        validateWrapper,
        showAlert,
        showLoader: setShowLoader,
        closeAlert,
      },
      isResetForm,
      navigate
    );
  };

  return (
    <>
      <CenterForm>
        <form onSubmit={handleEmailSubmit} className="formStyle">
          <div style={inputContainerStyle}>
            <InputStyled
              type="email"
              id="email"
              autoComplete="email"
              name="email"
              placeholder="Enter your email"
              value={forgotFormData.email}
              onChange={handleChange}
              onBlur={() =>
                handleBlurWrapper("email", forgotFormData.email ?? "")
              }
              $errorColor={errors.email ? "red" : ""}
            />
            {errors.email && <ErrorMessage>{errors.email}</ErrorMessage>}
          </div>
          <OTPAndBtn style={inputContainerStyle}>
            <div style={{ width: "48%" }}>
              <InputStyled
                className="no-spinner"
                type="number"
                id="code-forgot-password"
                autoComplete="one-time-code"
                name="otp"
                placeholder="OTP code"
                value={forgotFormData.otp}
                onChange={handleChange}
                onBlur={() =>
                  handleBlurWrapper("otp", forgotFormData.otp ?? "")
                }
                onKeyDown={(e) => {
                  if (e.key === "e" || e.key === "E" || e.key === ".") {
                    e.preventDefault();
                  }
                }}
                $errorColor={errors.otp ? "red" : ""}
              />
              {errors.otp && <ErrorMessage>{errors.otp}</ErrorMessage>}
            </div>
            <RequestOTPButton<ResetFormData>
              email={forgotFormData.email ?? ""}
              showAlert={showAlert}
              closeAlert={closeAlert}
              handleChange={handleChange} // Pass handleChange
              validateFieldWrapper={validateFieldWrapper} // Pass validation function
              errors={errors} // Pass errors object
              purpose="password_reset"
            />
          </OTPAndBtn>

          <Squarebtn
            ref={forgotPasswordRefBtn}
            type="submit"
            height="54px"
            width="100%"
            $bacgrndColor={styledComponentTheme.text}
            $color={styledComponentTheme.circularIconTextBg}
            $margin="0 0 20px 0"
          >
            Proceed
          </Squarebtn>

          <CenterNavTxt>
            <p onClick={handleLoginNav}>
              Go back to <span>Login</span>
            </p>
          </CenterNavTxt>
        </form>
      </CenterForm>

      {/* Show Loader while submitting */}
      {LoaderComponent}
    </>
  );
};

export const ResetForm: React.FC<{ isResetForm: boolean }> = ({
  isResetForm,
}) => {
  const navigate = useNavigate();
     const { showAlert, closeAlert } = useAuth();//global alert
   const { setShowLoader, LoaderComponent } = useLoader();
  const resetBtnRef = useRef<HTMLButtonElement>(null);
  const styledComponentTheme = styledTheme();
  //ripple effect
  useRippleEffect(resetBtnRef);

  const handleOTPNav = () => {
    navigate("/dashboard/forgot-password");
  };

  //initial form data
  const [resetFormData, setResetFormData] = useState<Partial<ResetFormData>>({
    newPassword: "",
    confirmPassword: "",
  });

  //initial errors
  const [errors, setErrors] = useState<Partial<ResetFormErrors>>({
    newPassword: "",
    confirmPassword: "",
  });

  //initial touched state
  const [touched, setTouched] = useState<Partial<ResetFormTouched>>({
    newPassword: false,
    confirmPassword: false,
  });

  // Handle input changes
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;

    setResetFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // console.log("Input Value", value);
    validateFieldWrapper(name as keyof ResetFormData, value);
  };

  const handleBlurWrapper = (
    name: keyof ResetFormData,
    value: string | number
  ) => handleResetBlur(name, value, touched, setTouched, validateFieldWrapper);

  const validateFieldWrapper = (
    name: keyof ResetFormData,
    value: string | number
  ) => {
    resetValidateField(name, value, errors, setErrors, resetFormData);
  };

  const validateWrapper = () =>
    resetValidate(resetFormData, errors, setErrors, setTouched);

  const handleResetPassword = (e: React.FormEvent) => {
    e.preventDefault();
    handleForgotAndResetSubmit(
      resetFormData,
      setResetFormData,
      {
        setTouched,
        validateWrapper,
        showAlert,
        showLoader: setShowLoader,
        closeAlert,
      },
      isResetForm,
      navigate
    );
  };

  return (
    <>
      <CenterForm>
        <form onSubmit={handleResetPassword} className="formStyle">
          <PasswordInput
            id={"newPassword"}
            name={"newPassword"}
            label={"New password"}
            placeholder={"Enter your new password"}
            value={resetFormData.newPassword ?? ""}
            onChange={handleChange}
            onBlurWrapper={handleBlurWrapper}
            error={errors.newPassword}
          />
          <PasswordInput
            id={"confirmPassword"}
            name={"confirmPassword"}
            label={"Confirm password"}
            placeholder={"Confirm your new password"}
            value={resetFormData.confirmPassword ?? ""}
            onChange={handleChange}
            onBlurWrapper={handleBlurWrapper}
            error={errors.confirmPassword}
          />

          <div style={endAlignment}>
            <Squarebtn
              ref={resetBtnRef}
              type="submit"
              height="50px"
              width="120px"
              $bacgrndColor={styledComponentTheme.text}
              $color={styledComponentTheme.circularIconTextBg}
            >
              Reset
            </Squarebtn>
          </div>
          <CenterNavTxt>
            <p onClick={handleOTPNav}>Go back</p>
          </CenterNavTxt>
        </form>
      </CenterForm>

      {/* Show Loader while submitting */}
      {LoaderComponent}
    </>
  );
};
